import _ from "lodash";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import classes from "./CollaboratorV2.scss";
import { connect } from "react-redux";
import CollaboratorV2AddEditModal from "./components/CollaboratorV2AddEditModal";
import {
  COLLABORATOR_V2_SEARCH_WITH_SELECTED_ITEMS,
  COLLABORATOR_V2_ADD_EDIT_MODAL,
  COLLABORATOR_V2_LIST_WITH_MODAL,
  COLLABORATOR_TYPES,
  SEARCH_LIST_TYPE_COLLABORATOR_TYPE_MAP,
} from "AppComponents/CollaboratorV2/modules/constants/stringConstants";
import CollaboratorV2SearchDropdownList from "./components/CollaboratorV2SearchDropdownList";
import CollaboratorV2ListWithModal from "./components/CollaboratorV2ListWithModal";
import { CollaboratorV2Context } from "AppComponents/CollaboratorV2/modules/Modules";
import CollaboratorV2AddEditUserModal from "AppComponents/CollaboratorV2/components/CollaboratorV2AddEditUserModal";
import { ROLE_VISITOR } from "Constants/stringConstants";
import { getProjectGroupCategoryIdByType } from "Projects/modules/Utils";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { checkIsProjectCoordinator } from "Projects/config/constants";
import { getCurrentProjectGroupDetails } from "Projects/modules/ProjectModules";

const CollaboratorV2 = props => {
  const {
    currentUserInfo, // using this, owner is decided
    updateInputField,
    variant, // to decide which vairant to render
    setCurrentUserAsDefaultCollaborator, // if need to set currentUser as default collaborator
    value, // already selected collaborators
    onCloseAddEditModal,
    name,
    searchListTypes,
    labelsConfig,
    customRef,
    showCurriculum,
    disabled,
    projectGroupId,
    required,
    showUnsavedChangesWarning,
    preventRemovingAllCollaborators,
    clickToExpand,
    showSearchDropdownListHeader,
    searchDropdownListContainerStyle,
    hideCollaborators,
    updateDoneButtonDisabled,
    collaboratorContainerStyle,
    setCurrentUserAsOwner, //to set current user as owner, which means the current user cant remove himself
    isDropdownFocusedOnMount,
    ownerId,
    isAddingNewCollaboratorEnabled,
    approvedExternalCollaborators,
    projectId,
    testId,
  } = props;

  const collaboratorType = useMemo(() => {
    let res = "";
    _.forEach(COLLABORATOR_TYPES, type => {
      const valueArray = SEARCH_LIST_TYPE_COLLABORATOR_TYPE_MAP[type];

      if (_.size(_.intersection(valueArray, searchListTypes)) != 0) {
        res = type;
        return;
      }
    });

    return res;
  }, [searchListTypes]);

  const [selectedCollaborators, setSelectedCollaborators] = useState(() => {
    const { userType: currentUserType } = currentUserInfo;

    const defaultCollaborators = _.isEqual(currentUserType, collaboratorType)
      ? [currentUserInfo]
      : [];

    return setCurrentUserAsDefaultCollaborator
      ? defaultCollaborators
      : value ?? [];
  });

  const [isError, setIsError] = useState(false);

  const [addEditUserModalDetails, setAddEditUserModalDetails] = useState({
    displayModal: false,
  });

  const updateAddEditUserModalDetails = useCallback(details => {
    setAddEditUserModalDetails(details);
  }, []);

  const onCloseAddEditUserModal = () => {
    updateAddEditUserModalDetails({ displayModal: false });
  };

  const valuesStringified = JSON.stringify(value);

  useEffect(() => {
    if (!setCurrentUserAsDefaultCollaborator && value)
      setSelectedCollaborators(value);
  }, [valuesStringified]);

  const updateSelectedCollaborators = collaborators => {
    if (_.isEqual(collaborators, selectedCollaborators)) return;

    setSelectedCollaborators(collaborators);

    const updatedCollaborators = _.map(collaborators, item => {
      return _.omit(item, ["icon", "courses"]);
    });

    if (name) {
      updateInputField({
        [name]: updatedCollaborators,
      });
    } else {
      updateInputField(updatedCollaborators);
    }
  };

  const removeCollaborators = removedId => {
    const updatedCollaborators = _.filter(
      selectedCollaborators,
      item => item.id != removedId
    );

    updateSelectedCollaborators(updatedCollaborators);
  };

  const isValid = () => {
    if (_.size(selectedCollaborators) == 0) {
      setIsError(true);
      return 1;
    }
    setIsError(false);
    return 0;
  };

  // if some other fns/values are required in the ref
  // convert the fn to normal function and pass 'this' keyword to the ref

  if (customRef) {
    customRef({
      isValid,
    });
  }

  const labels = useMemo(() => {
    return {
      headerText: _.get(
        labelsConfig,
        "headerText",
        "project:student_plural_parenthesis"
      ),
      headerSubText: _.get(
        labelsConfig,
        "headerSubText",
        "project:add_students_to_supervise_on_this_service_activity"
      ),
      modalHeaderText: _.get(
        labelsConfig,
        "modalHeaderText",
        "common:add_with_label"
      ),
      modalHeaderTextLocale: _.get(
        labelsConfig,
        "modalHeaderTextLocale",
        "common:collaborator"
      ),
      modalHeaderSubText: _.get(labelsConfig, "modalHeaderSubText", ""),
      modalFooterCountText: _.get(
        labelsConfig,
        "modalFooterCountText",
        "common:collaborator_with_count"
      ),
      modalListHeaderText: _.get(labelsConfig, "modalListHeaderText", ""),
      modalListHeaderTextLocale: _.get(
        labelsConfig,
        "modalListHeaderTextLocale",
        "common:collaborator"
      ),
      pendingApprovalText: _.get(
        labelsConfig,
        "pendingApprovalsText",
        "project:coordinators_approval_pending"
      ),
      unsavedChangesModalHeader: _.get(
        labelsConfig,
        "unsavedChangesModalHeader",
        "common:unsaved_changes"
      ),
      unsavedChangesModalBody: _.get(
        labelsConfig,
        "unsavedChangesModalBody",
        "project:the_changes_you_have_made_will_be_discarded_with_label"
      ),
      unsavedChangesModalBodyLocale: _.get(
        labelsConfig,
        "unsavedChangesModalBodyLocale",
        "common:collaborators"
      ),
      searchDropdownListTitle: _.get(
        labelsConfig,
        "searchDropdownListTitle",
        "common:collaborator_plural_with_bracket_s"
      ),
      searchDropdownListSubtitle: _.get(
        labelsConfig,
        "searchDropdownListSubtitle",
        "common:add_with_label"
      ),
      searchDropdownListSubtitleLocale: _.get(
        labelsConfig,
        "modalHeaderTextLocale",
        "common:collaborator"
      ),
      disableCollaboratorRemovalTooltip: _.get(
        labelsConfig,
        "disableCollaboratorRemovalTooltip",
        "common:there_should_be_atleast_one_collaborator_selected"
      ),
      addEditUserModalHeaderText: _.get(
        labelsConfig,
        "addEditUserModalHeaderText",
        "project:invite_supervisor"
      ),
      addEditUserModalSubHeaderText: _.get(
        labelsConfig,
        "addEditUserModalSubHeaderText",
        "project:an_approval_request_will_be_sent_to_your_CAS_coordinator"
      ),
      addEditUserModalDoneButtonText: _.get(
        labelsConfig,
        "addEditUserModalDoneButtonText",
        "project:raise_a_request"
      ),
      noSelectedCollaboratorsEmptyStateText: _.get(
        labelsConfig,
        "noSelectedCollaboratorsEmptyStateText",
        ""
      ),
      noSelectedCollaboratorsEmptyStateSubText: _.get(
        labelsConfig,
        "noSelectedCollaboratorsEmptyStateSubText",
        ""
      ),
      projectTypeLabel: _.get(
        labelsConfig,
        "projectTypeLabel",
        "common:activity"
      ),
    };
  }, [labelsConfig]);

  const getComponentToRender = () => {
    switch (variant) {
      case COLLABORATOR_V2_LIST_WITH_MODAL: {
        return (
          <CollaboratorV2ListWithModal isError={isError} testId={testId} />
        );
      }
      case COLLABORATOR_V2_SEARCH_WITH_SELECTED_ITEMS: {
        return (
          <CollaboratorV2SearchDropdownList
            selectedOptions={value ?? []}
            onRemoveOption={removeCollaborators}
            onAddOptions={updateSelectedCollaborators}
            showListHeader={false}
            clickToExpand={clickToExpand}
            showSearchDropdownListHeader={showSearchDropdownListHeader}
            searchDropdownListContainerStyle={searchDropdownListContainerStyle}
            updateDoneButtonDisabled={updateDoneButtonDisabled}
            testId={testId}
          />
        );
      }
      case COLLABORATOR_V2_ADD_EDIT_MODAL: {
        return (
          <CollaboratorV2AddEditModal
            onCloseModal={onCloseAddEditModal}
            closeModalOnDone={true}
          />
        );
      }

      default:
        throw new Error("invalid variant type");
    }
  };

  return (
    <>
      <CollaboratorV2Context.Provider
        value={{
          disabled:
            disabled ||
            _.isEqual(_.get(currentUserInfo, "userRole"), ROLE_VISITOR),
          variant,
          value,
          projectGroupId,
          collaboratorType,
          currentUserInfo,
          showCurriculum,
          labels,
          searchListTypes,
          required,
          showUnsavedChangesWarning,
          preventRemovingAllCollaborators,
          removeCollaborators,
          updateSelectedCollaborators,
          selectedCollaborators,
          hideCollaborators,
          setCurrentUserAsOwner,
          isDropdownFocusedOnMount,
          ownerId,
          isAddingNewCollaboratorEnabled,
          name,
          updateAddEditUserModalDetails,
          approvedExternalCollaborators,
          projectId,
        }}
      >
        <div className={classes.container} style={collaboratorContainerStyle}>
          {getComponentToRender()}
        </div>
      </CollaboratorV2Context.Provider>
      {addEditUserModalDetails.displayModal ? (
        <CollaboratorV2AddEditUserModal
          {...addEditUserModalDetails}
          modalHeaderText={labels.addEditUserModalHeaderText}
          modalSubHeaderText={labels.addEditUserModalSubHeaderText}
          modalDoneButtonText={labels.addEditUserModalDoneButtonText}
          projectGroupId={projectGroupId}
          onClose={onCloseAddEditUserModal}
          onCloseAddEditModal={onCloseAddEditModal}
        />
      ) : null}
    </>
  );
};

const mapStateToProps = state => {
  const userInfo = getUserInfo();

  const {
    first_name: firstName = "",
    middle_name: middleName = "",
    last_name: lastName = "",
    profile_image = null,
    id = "",
    role: userRole,
    user_type: type,
    suffix,
    pronouns,
    preferred_name: preferredName,
    org_id: organizationId,
  } = userInfo;

  const curriculumProgram = _.get(
    state,
    "platform.currentCurriculumProgram",
    null
  );
  const {
    id: currentCurriculumProgramId,
    type: currentCurriculumProgramType,
  } = curriculumProgram;

  const userType = type === "staff" ? "SUPERVISOR" : "STUDENT";

  const projectGroupCategoryIdByType = getProjectGroupCategoryIdByType({
    curriculumProgramIds: currentCurriculumProgramId
      ? [currentCurriculumProgramId]
      : [],
    organizationId,
    curriculumType: currentCurriculumProgramType,
  });

  const { type: projectGroupType } = getCurrentProjectGroupDetails({
    state,
  });

  const projectGroupCategoryId = projectGroupCategoryIdByType[projectGroupType];
  const isUserProjectCoordinator = checkIsProjectCoordinator({
    projectGroupCategoryId,
    curriculumProgramId: currentCurriculumProgramId,
  });

  return {
    currentUserInfo: {
      firstName,
      middleName,
      lastName,
      profileImage: profile_image,
      id,
      userType,
      userRole,
      type,
      suffix,
      pronouns,
      preferredName,
      isUserProjectCoordinator,
    },
  };
};

export default connect(mapStateToProps)(CollaboratorV2);

CollaboratorV2.defaultProps = {
  showCurriculum: false,
  setCurrentUserAsDefaultCollaborator: false,
  value: [],
  showUnsavedChangesWarning: true,
  preventRemovingAllCollaborators: false,
  showSearchDropdownListHeader: false,
  searchDropdownListContainerStyle: {},
  clickToExpand: false,
  labelsConfig: {},
  hideCollaborators: [],
  collaboratorContainerStyle: {},
  setCurrentUserAsOwner: false,
  isDropdownFocusedOnMount: true,
  ownerId: "",
  isAddingNewCollaboratorEnabled: false,
};
